import React, { useEffect, useState, useCallback } from "react";
import { Button } from "primereact/button";
import {
  jumpToNextHighlight,
  jumpToPreviousHighlight,
  getHighlightNavigationInfo,
} from "../../utils/searchHighlight";
import styles from "./FloatingSearchNavigation.module.css";

interface FloatingSearchNavigationProps {
  visible: boolean;
}

export const FloatingSearchNavigation: React.FC<
  FloatingSearchNavigationProps
> = ({ visible }) => {
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [navigationInfo, setNavigationInfo] = useState({
    current: 0,
    total: 0,
  });

  const updateNavigationInfo = useCallback(() => {
    const info = getHighlightNavigationInfo(currentIndex);
    setNavigationInfo(info);
  }, [currentIndex]);

  useEffect(() => {
    if (visible) {
      // Reset to first highlight when becoming visible
      setCurrentIndex(-1);
      updateNavigationInfo();

      // Create MutationObserver to watch for DOM changes
      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;

        mutations.forEach((mutation) => {
          // Check if any added/removed nodes contain search highlights
          const checkNodes = (nodes: NodeList) => {
            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i];
              if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as Element;
                if (
                  element.matches?.("mark.search-highlight-text") ||
                  element.querySelector?.("mark.search-highlight-text")
                ) {
                  shouldUpdate = true;
                  break;
                }
              }
            }
          };

          if (mutation.addedNodes.length > 0) {
            checkNodes(mutation.addedNodes);
          }
          if (mutation.removedNodes.length > 0) {
            checkNodes(mutation.removedNodes);
          }

          // Check for class changes on existing highlight elements
          if (
            mutation.type === "attributes" &&
            mutation.attributeName === "class" &&
            mutation.target instanceof Element &&
            mutation.target.matches("mark.search-highlight-text")
          ) {
            shouldUpdate = true;
          }
        });

        if (shouldUpdate) {
          // Small delay to ensure DOM is fully updated
          setTimeout(updateNavigationInfo, 50);
        }
      });

      // Start observing the document for changes
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["class"],
      });

      return () => observer.disconnect();
    } else {
      // Reset when not visible
      setCurrentIndex(-1);
      setNavigationInfo({ current: 0, total: 0 });
    }
  }, [visible, updateNavigationInfo]);

  const handleNextHighlight = () => {
    const newState = jumpToNextHighlight(currentIndex);
    if (newState) {
      setCurrentIndex(newState.currentIndex);
      setNavigationInfo({
        current: newState.currentIndex + 1,
        total: newState.total,
      });
    }
  };

  const handlePreviousHighlight = () => {
    const newState = jumpToPreviousHighlight(currentIndex);
    if (newState) {
      setCurrentIndex(newState.currentIndex);
      setNavigationInfo({
        current: newState.currentIndex + 1,
        total: newState.total,
      });
    }
  };

  if (!visible || navigationInfo.total === 0) {
    return null;
  }

  return (
    <div className={styles.floatingNavigation}>
      <div className={styles.navigationContent}>
        <span className={styles.navigationText}>
          {navigationInfo.current} of {navigationInfo.total}
        </span>
        <div className={styles.navigationButtons}>
          <Button
            icon="pi pi-angle-up"
            className="p-button-sm p-button-text"
            onClick={handlePreviousHighlight}
            disabled={navigationInfo.total === 0}
            type="button"
          />
          <Button
            icon="pi pi-angle-down"
            className="p-button-sm p-button-text"
            onClick={handleNextHighlight}
            disabled={navigationInfo.total === 0}
            type="button"
          />
        </div>
      </div>
    </div>
  );
};
